/**
 * Diagnostics attached to `[AuthFormError]` logs, to find out why a login/register Server Action
 * fails with "An unexpected response was received from the server." Next doesn't expose the HTTP
 * response of a failed action, no access log records these POSTs, and Resource Timing is no help
 * either (Chrome only records an entry once the body is read, which Next's error path never does).
 * So while the action is in flight we observe `window.fetch` and remember what the Server Action
 * request — the one carrying a `next-action` header — got back.
 *
 * Deliberately contains nothing sensitive: no email/password, no request body, no query strings,
 * and token path segments are redacted (`/auto-login/<token>`, `/impersonate/<token>`,
 * `/r/consent/<token>`).
 */
export interface ActionResponseInfo {
  status: number;
  redirected: boolean;
  /** Path the response finally came from (differs from the page when a redirect was followed). */
  finalPath: string;
  contentType: string | null;
}

/** What one observation saw while its action was in flight. */
export interface ObservedActions {
  /** Last Server Action response seen, or null (e.g. a network error before any response). */
  response: ActionResponseInfo | null;
  responseCount: number;
  /** Another form's action was observed at the same time — `response` may not be this form's. */
  concurrent: boolean;
}

export interface AuthFormDiagnostics extends ObservedActions {
  formKind: string;
  path: string;
  pageAgeMs: number;
  online: boolean | null;
}

interface Observation {
  responses: ActionResponseInfo[];
  concurrent: boolean;
}

const activeObservations = new Set<Observation>();
let sharedWrapper: { original: typeof window.fetch; wrapper: typeof window.fetch } | null = null;

/**
 * Installs the single shared fetch wrapper. Responses pass through unchanged. No-op while ours is
 * the active `window.fetch`; otherwise (never installed, or someone replaced fetch after us) a fresh
 * wrapper goes on top. Only the current wrapper records, so a stale one left deeper in a chain is
 * inert and nothing is recorded twice.
 */
const installSharedWrapper = () => {
  if (sharedWrapper && window.fetch === sharedWrapper.wrapper) return;
  const original = window.fetch;
  const wrapper: typeof window.fetch = async (input, init) => {
    const response = await original(input, init);
    const isCurrent = sharedWrapper?.wrapper === wrapper;
    if (isCurrent && activeObservations.size > 0 && hasNextActionHeader(input, init)) {
      try {
        const info = describeActionResponse(response);
        activeObservations.forEach((observation) => { observation.responses.push(info); });
      } catch {
        // Diagnostics must never affect the request.
      }
    }
    return response;
  };
  window.fetch = wrapper;
  sharedWrapper = { original, wrapper };
};

/**
 * Restores the original fetch — unless something wrapped it after us, in which case our wrapper
 * stays in that chain as a pure pass-through (it records nothing without active observations).
 */
const uninstallSharedWrapper = () => {
  if (sharedWrapper && window.fetch === sharedWrapper.wrapper) {
    window.fetch = sharedWrapper.original;
  }
  sharedWrapper = null;
};

const TOKEN_SEGMENT = /\/(auto-login|impersonate|r\/consent)\/[^/]+/g;

export const redactAuthPath = (pathname: string): string => pathname.replace(TOKEN_SEGMENT, '/$1/<token>');

const hasNextActionHeader = (input: RequestInfo | URL, init?: RequestInit): boolean => {
  try {
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    return headers.has('next-action');
  } catch {
    return false;
  }
};

export const describeActionResponse = (response: Pick<Response, 'status' | 'redirected' | 'url' | 'headers'>): ActionResponseInfo => {
  let finalPath = '';
  try {
    finalPath = redactAuthPath(new URL(response.url).pathname);
  } catch {
    // Opaque/empty URL — leave the path empty.
  }

  return {
    status: response.status,
    redirected: response.redirected,
    finalPath,
    contentType: response.headers.get('content-type'),
  };
};

/**
 * Starts observing Server Action responses. Read-only: every response is returned unchanged.
 * Call `stop()` when the action settles; it returns what this observation saw.
 *
 * Several forms can observe at once (e.g. the OAuth page mounts one `BaseAuthForm` per provider),
 * so all observations share ONE fetch wrapper, installed while any observation is active. Next
 * queues Server Actions and runs them one at a time, so an observation that never overlapped
 * another saw exactly its own request. When they did overlap, a response can't be attributed to
 * a specific form: `concurrent` says so, so the log entry is read as ambiguous instead of wrong.
 */
export const observeActionResponses = (): { stop: () => ObservedActions } => {
  const observation: Observation = { responses: [], concurrent: activeObservations.size > 0 };
  activeObservations.forEach((other) => { other.concurrent = true; });
  activeObservations.add(observation);
  installSharedWrapper();

  return {
    stop: () => {
      activeObservations.delete(observation);
      if (activeObservations.size === 0) uninstallSharedWrapper();
      return {
        response: observation.responses.at(-1) ?? null,
        responseCount: observation.responses.length,
        concurrent: observation.concurrent,
      };
    },
  };
};

export const buildAuthFormDiagnostics = (
  formKind: string,
  observed: ObservedActions,
  env: { pathname: string; pageAgeMs: number; online: boolean | undefined },
): AuthFormDiagnostics => ({
  formKind,
  path: redactAuthPath(env.pathname),
  pageAgeMs: Math.round(env.pageAgeMs),
  online: typeof env.online === 'boolean' ? env.online : null,
  ...observed,
});

/** Reads the live browser state for the log entry. */
export const collectAuthFormDiagnostics = (formKind: string, observed: ObservedActions): AuthFormDiagnostics => buildAuthFormDiagnostics(
  formKind,
  observed,
  { pathname: window.location.pathname, pageAgeMs: performance.now(), online: navigator.onLine },
);
